import { useState } from "react";
import { usePageSeo } from "@/hooks/usePageSeo";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Send, MessageCircle, Headphones, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import heroContactImg from "@/assets/hero-contact.jpg";

const ContactPage = () => {
  usePageSeo("contact");
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const { getSetting } = useSiteSettings();

  const phone = getSetting("phone", "+1 (809) 123-4567");
  const email = getSetting("email", "info@worldtransferstrips.com");
  const address = getSetting("address", "Dominican Republic");
  const whatsappNumber = getSetting("whatsapp_number", "18091234567");

  const contactInfo = [
    { icon: Phone, label: "Phone", value: phone, href: `tel:${phone.replace(/\s/g, "")}` },
    { icon: Mail, label: "Email", value: email, href: `mailto:${email}` },
    { icon: MapPin, label: "Location", value: address, href: null },
    { icon: Clock, label: "Hours", value: "24/7 — Always available", href: null },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setTimeout(() => {
      toast({ title: "Message sent!", description: "We'll get back to you shortly." });
      setFormData({ name: "", email: "", message: "" });
      setSending(false);
    }, 1000);
  };

  return (
    <main className="min-h-screen bg-background">
      {/* Hero */}
      <section className="relative h-[60vh] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${heroContactImg})` }} />
        <div className="absolute inset-0 bg-[hsl(var(--hero-bg))]/80" />
        <Navbar />
        <div className="container mx-auto px-4 relative z-10 h-full flex items-center pt-16">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-secondary/20 border border-secondary/30 px-4 py-2 rounded-none mb-6">
              <Headphones className="w-4 h-4 text-secondary" />
              <span className="text-hero-foreground text-sm font-sans font-medium uppercase tracking-wider">24/7 Support</span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-extrabold text-hero-foreground mb-6 leading-tight">
              Get In <span className="text-secondary">Touch</span>
            </h1>
            <p className="text-hero-foreground/80 font-sans text-xl leading-relaxed max-w-2xl">
              Need help with your tour or transfer? We're here to provide instant support whenever you need it.
            </p>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-secondary" />
      </section>

      {/* Quick Contact Strip */}
      <section className="bg-primary py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-8">
            {[
              { icon: Zap, label: "Instant Response", desc: "Within 5 minutes" },
              { icon: MessageCircle, label: "Live Chat", desc: "WhatsApp & Web" },
              { icon: Phone, label: "Direct Line", desc: "Call anytime" }
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-none bg-secondary flex items-center justify-center">
                  <item.icon className="w-5 h-5 text-primary-foreground" />
                </div>
                <div>
                  <div className="font-display font-bold text-primary-foreground text-sm">{item.label}</div>
                  <div className="text-primary-foreground/60 text-xs font-sans">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-5xl mx-auto">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-2">Contact Information</h2>
              <div className="w-12 h-1 bg-secondary mb-8" />
              <div className="space-y-4">
                {contactInfo.map((item, i) => (
                  <motion.div key={item.label} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-4 p-5 rounded-none bg-card border border-border hover:border-secondary/40 hover:shadow-lg transition-all duration-300">
                    <div className="w-12 h-12 rounded-none bg-primary flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div>
                      <p className="font-sans font-bold text-foreground text-sm">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} className="text-muted-foreground font-sans text-sm hover:text-secondary transition-colors">{item.value}</a>
                      ) : (
                        <p className="text-muted-foreground font-sans text-sm">{item.value}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              <a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-3 bg-[hsl(142,70%,40%)] text-white font-sans font-bold text-base px-8 py-4 rounded-none hover:opacity-90 transition-all duration-300 w-full justify-center">
                <MessageCircle className="w-5 h-5" />
                Start WhatsApp Chat
              </a>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-2">Send a Message</h2>
              <div className="w-12 h-1 bg-secondary mb-8" />
              <form onSubmit={handleSubmit} className="space-y-5 p-8 rounded-none bg-card border border-border shadow-lg">
                <div>
                  <label className="font-sans text-sm font-semibold text-foreground mb-2 block">Your Name</label>
                  <Input placeholder="John Doe" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="font-sans h-12 rounded-none" />
                </div>
                <div>
                  <label className="font-sans text-sm font-semibold text-foreground mb-2 block">Email Address</label>
                  <Input type="email" placeholder="john@example.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="font-sans h-12 rounded-none" />
                </div>
                <div>
                  <label className="font-sans text-sm font-semibold text-foreground mb-2 block">Your Message</label>
                  <Textarea placeholder="Tell us about your travel plans..." value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} required rows={5} className="font-sans rounded-none" />
                </div>
                <Button type="submit" disabled={sending} className="w-full h-12 gap-2 font-sans font-bold rounded-none bg-secondary hover:bg-secondary/90 text-secondary-foreground">
                  <Send className="w-4 h-4" />
                  {sending ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default ContactPage;
